import React from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";

const getDialogTitle = (cancelConfirmationType) => {
  if (cancelConfirmationType === "upload") return "Cancel Upload";
  if (cancelConfirmationType === "delete") return "Delete Image";
  if (cancelConfirmationType === "editor") {
    return "Are you sure you want to go back to the Editor?";
  }
  return "";
};

const getDialogMessage = (cancelConfirmationType) => {
  if (cancelConfirmationType === "upload") {
    return "Are you sure you want to cancel the upload? Any unsaved progress will be lost.";
  }
  if (cancelConfirmationType === "delete") {
    return "Are you sure you want to delete this image?";
  }
  if (cancelConfirmationType === "editor") {
    return "If you go back to the editor, you will exit the flow and return to the editor.";
  }
  return "";
};

const getConfirmHandler = ({
  cancelConfirmationType,
  onConfirmUploadCancel,
  onConfirmDeleteImage,
  onConfirmBackToEditor,
}) => {
  if (cancelConfirmationType === "upload") return onConfirmUploadCancel;
  if (cancelConfirmationType === "delete") return onConfirmDeleteImage;
  if (cancelConfirmationType === "editor") return onConfirmBackToEditor;
  return null;
};

export default function BackupImageCancelConfirmationDialog({
  cancelConfirmationType,
  selectedFileName,
  onDialogClose,
  onClose,
  onConfirmUploadCancel,
  onConfirmDeleteImage,
  onConfirmBackToEditor,
  getOriginalFilename
}) {
  const confirmHandler = getConfirmHandler({
    cancelConfirmationType,
    onConfirmUploadCancel,
    onConfirmDeleteImage,
    onConfirmBackToEditor,
  });
  const confirmInFlightRef = React.useRef(false);

  React.useEffect(() => {
    confirmInFlightRef.current = false;
  }, [cancelConfirmationType]);

  const handleConfirm = React.useCallback(() => {
    if (
      !cancelConfirmationType ||
      confirmInFlightRef.current ||
      !confirmHandler
    ) {
      return;
    }
    confirmInFlightRef.current = true;
    confirmHandler();
  }, [cancelConfirmationType, confirmHandler]);

  return (
    <Dialog
      open={Boolean(cancelConfirmationType)}
      onClose={onDialogClose}
      disableEscapeKeyDown
      maxWidth="sm"
      className="backup-cancel-confirmation-dialog"
      aria-labelledby="backup-cancel-confirmation-title"
      BackdropProps={{ className: "backup-cancel-confirmation-backdrop" }}
    >
      <div
        className="backup-cancel-confirmation-top-strip"
        aria-hidden="true"
      />
      <DialogTitle
        id="backup-cancel-confirmation-title"
        className="backup-cancel-confirmation-dialog-title"
      >
        <div className="backup-cancel-confirmation-header">
          <div className="backup-cancel-confirmation-title">
            {getDialogTitle(cancelConfirmationType)}
          </div>
        </div>
      </DialogTitle>
      <DialogContent className="backup-cancel-confirmation-content">
        <div className="backup-cancel-confirmation-message-wrap">
          <div className="backup-cancel-confirmation-message">
            {getDialogMessage(cancelConfirmationType)}
          </div>
          {cancelConfirmationType === "delete" && selectedFileName ? (
            <Tooltip
              title={getOriginalFilename(selectedFileName)}
              enterDelay={0}
              enterNextDelay={0}
              placement="bottom-start"
              arrow
              PopperProps={{
                modifiers: [
                  {
                    name: "offset",
                    options: {
                      offset: [0, 8],
                    },
                  },
                ],
              }}
            >
              <div className="backup-cancel-confirmation-file-name">
                {getOriginalFilename(selectedFileName)}
              </div>
            </Tooltip>
          ) : null}
        </div>
      </DialogContent>
      <DialogActions className="backup-cancel-confirmation-actions">
        <button
          type="button"
          className="backup-cancel-confirmation-no"
          onClick={onClose}
        >
          No
        </button>
        <button
          type="button"
          className="backup-cancel-confirmation-yes"
          onClick={handleConfirm}
        >
          Yes
        </button>
      </DialogActions>
    </Dialog>
  );
}
